/* Phase 5B-3 + 6B-1a — ErrorBoundary + PWA install banner + 502 retry interceptor + AI Cost tile.
 *
 * 6B-1a additions:
 *   - AICostTile now fetches `/ai/budget-status` and renders a coloured budget chip.
 *   - Server-side log warning at ≥ 90% lives in backend/routes/ai_budget.py.
 */
import React from "react";
import { Sparkles, Download, X, AlertOctagon, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// ============================================================
// ErrorBoundary
// ============================================================
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null, info: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    this.setState({ info });
    try { api.post("/audit", { action: "frontend_error", entity: "ui", after: { message: String(err), stack: String(err?.stack || "").slice(0, 1500), componentStack: String(info?.componentStack || "").slice(0, 1500), url: window.location.href } }).catch(() => {}); } catch {}
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div data-testid="error-boundary" className="min-h-screen flex items-center justify-center p-6 bg-[var(--surface)]">
        <div className="max-w-md w-full glass rounded-2xl p-6 text-center border border-red-500/30">
          <AlertOctagon className="h-10 w-10 mx-auto text-red-400 mb-3" />
          <div className="font-heading text-lg tracking-wider mb-2">SOMETHING BROKE</div>
          <div className="text-xs text-foreground/70 mb-4">The UI hit an unexpected error. We've logged it. Try a reload — and let your admin know if it keeps happening.</div>
          <div className="text-[10px] font-mono text-foreground/40 bg-white/3 p-2 rounded mb-4 text-left max-h-32 overflow-y-auto">{String(this.state.err).slice(0, 200)}</div>
          <button onClick={() => window.location.reload()} data-testid="error-reload-btn" className="w-full py-2 rounded-lg bg-gradient-to-br from-brand-blue to-sky-400 text-white text-sm">
            <RotateCw className="h-3.5 w-3.5 inline mr-1.5" />Reload
          </button>
        </div>
      </div>
    );
  }
}

// ============================================================
// PWA install banner
// ============================================================
export function PWAInstallBanner() {
  const [evt, setEvt] = React.useState(null);
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    if (localStorage.getItem("pwa_dismissed") === "1") return;
    const handler = (e) => { e.preventDefault(); setEvt(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  if (!show || !evt) return null;
  const install = async () => {
    evt.prompt(); const r = await evt.userChoice;
    if (r.outcome === "accepted") toast.success("Installed!");
    setShow(false); localStorage.setItem("pwa_dismissed", "1");
  };
  return (
    <div data-testid="pwa-install-banner" className="rounded-xl p-3 border border-sky-500/40 bg-gradient-to-r from-brand-blue/20 to-sky-500/10 flex items-center gap-3 mb-3">
      <Sparkles className="h-5 w-5 text-sky-300 shrink-0" />
      <div className="flex-1">
        <div className="font-heading text-xs tracking-wider">INSTALL SHARDA ENERGY OS</div>
        <div className="text-[11px] text-foreground/70">One-tap launch · works offline · faster than the browser tab</div>
      </div>
      <button onClick={install} data-testid="pwa-install-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-brand-blue to-sky-400 text-white text-xs flex items-center gap-1.5"><Download className="h-3 w-3" />Install</button>
      <button onClick={() => { setShow(false); localStorage.setItem("pwa_dismissed", "1"); }} data-testid="pwa-dismiss-btn" className="h-7 w-7 rounded hover:bg-white/10 flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// ============================================================
// Budget chip — colour-coded ₹X / ₹Y · NN% used
// ============================================================
function BudgetChip({ budget }) {
  if (!budget) return null;
  const { spent_usd, budget_usd, pct_used, status, hint, source } = budget;
  // Status → tailwind classes
  const palette = {
    green:   { ring: "border-emerald-400/40",  fg: "text-emerald-300",  bg: "bg-emerald-500/15" },
    amber:   { ring: "border-amber-400/40",    fg: "text-amber-300",    bg: "bg-amber-500/15" },
    red:     { ring: "border-rose-400/50",     fg: "text-rose-300",     bg: "bg-rose-500/15" },
    unknown: { ring: "border-white/15",        fg: "text-foreground/70",bg: "bg-white/5" },
    blocked: { ring: "border-white/10",        fg: "text-foreground/50",bg: "bg-white/5" },
  }[status] || { ring: "border-white/15", fg: "text-foreground/70", bg: "bg-white/5" };

  const fmt = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
  const pct = pct_used == null ? "—" : `${pct_used}%`;
  return (
    <div
      data-testid="ai-budget-chip"
      data-status={status}
      className={`mt-3 rounded-xl border ${palette.ring} ${palette.bg} px-3 py-2`}
      title={hint || `Source: ${source || "n/a"}`}
    >
      <div className="flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className={`${palette.fg} font-mono`} data-testid="ai-budget-spent">{fmt(spent_usd)}</span>
          <span className="text-foreground/40">/</span>
          <span className={`${palette.fg} font-mono`} data-testid="ai-budget-cap">{fmt(budget_usd)}</span>
          <span className="text-foreground/40">·</span>
          <span className={`${palette.fg} font-medium`} data-testid="ai-budget-pct">{pct} used</span>
        </div>
        <span className={`uppercase tracking-widest text-[9px] font-heading ${palette.fg}`} data-testid="ai-budget-status">
          {status}
        </span>
      </div>
      {hint && (
        <div className="mt-1 text-[10px] text-foreground/60 leading-snug" data-testid="ai-budget-hint">
          {hint}
        </div>
      )}
    </div>
  );
}

// ============================================================
// AI Cost Tile (CEO home) — now with live budget chip
// ============================================================
export function AICostTile() {
  const [data, setData] = React.useState(null);
  const [budget, setBudget] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get("/ai/cost-summary").then(({ data }) => { if (alive) setData(data); }).catch(() => {});
    api.get("/ai/budget-status").then(({ data }) => { if (alive) setBudget(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!data || data.blocked) return null;
  return (
    <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-transparent p-4" data-testid="ai-cost-tile">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest font-heading text-purple-300">AI USAGE · {data.period}</div>
        <Sparkles className="h-4 w-4 text-purple-300/60" />
      </div>
      <div className="font-heading text-2xl mt-2 text-purple-200" data-testid="ai-cost-calls">{data.calls} calls</div>
      <div className="text-[11px] mt-1 text-purple-200/70" data-testid="ai-cost-amount">~{Math.round(data.tokens_est / 1000)}K tokens · est ${data.est_cost_usd}</div>
      <BudgetChip budget={budget} />
    </div>
  );
}
